import React, { useState, useEffect } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/**
 * AITaggingPanel — lets a firm describe, in plain language, how candidates
 * should be tagged. The text is injected into the AI assistant's system
 * prompt so it suggests/applies tags along the firm's taxonomy across all
 * chats.
 *
 * Two scopes:
 *   • Firm-wide (admins only) — the default everyone inherits.
 *   • Personal override — any user can tailor it for themselves; when set,
 *     it replaces the firm default for that user.
 */
export default function AITaggingPanel({ headers, isAdmin }) {
  const [firmText, setFirmText] = useState('');
  const [userText, setUserText] = useState('');
  const [hasUserOverride, setHasUserOverride] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingScope, setSavingScope] = useState(null); // 'firm' | 'user' | null
  const [savedScope, setSavedScope] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/assistant/tagging-guidance`, { headers });
      if (!r.ok) throw new Error('Failed to load');
      const d = await r.json();
      setFirmText(d.firm || '');
      setUserText(d.user || '');
      setHasUserOverride(!!d.hasUserOverride);
    } catch (e) {
      setError('Could not load tagging guidance.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async (scope) => {
    setSavingScope(scope);
    setSavedScope(null);
    setError(null);
    try {
      const r = await fetch(`${API_BASE_URL}/api/assistant/tagging-guidance`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ scope, guidance: scope === 'firm' ? firmText : userText }),
      });
      if (!r.ok) {
        const b = await r.json().catch(() => ({}));
        throw new Error(b.error || 'Save failed');
      }
      const d = await r.json();
      setFirmText(d.firm || '');
      setUserText(d.user || '');
      setHasUserOverride(!!d.hasUserOverride);
      setSavedScope(scope);
      setTimeout(() => setSavedScope(null), 2500);
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSavingScope(null);
    }
  };

  const placeholder =
    'Describe how candidates should be tagged. For example:\n\n' +
    'We tag candidates on three axes:\n' +
    '• Function — e.g. quant-research, swe, trading, sales\n' +
    '• Years of experience — yoe-0-3, yoe-3-7, yoe-7-plus\n' +
    '• Education — phd, masters, bachelors\n\n' +
    'Apply one tag from each axis. Prefer existing tags over creating new ones.';

  if (loading) {
    return <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" /></div>;
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h2 className="text-base font-semibold text-gray-900">AI Tagging Guidance</h2>
        <p className="text-sm text-gray-500 mt-1">
          Tell the assistant how your team tags candidates. It uses this to suggest and apply tags
          that follow your structure — and matches your existing tags before creating new ones.
        </p>
      </div>

      {error && (
        <div className="px-3 py-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded">{error}</div>
      )}

      {/* Firm-wide */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">Firm-wide taxonomy</h3>
            <p className="text-xs text-gray-500">Applies to everyone at the firm unless they set a personal override.</p>
          </div>
          {!isAdmin && <span className="text-[11px] text-gray-400">Admin only</span>}
        </div>
        <textarea
          value={firmText}
          onChange={(e) => setFirmText(e.target.value)}
          disabled={!isAdmin}
          rows={9}
          placeholder={placeholder}
          className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-y disabled:bg-gray-50 disabled:text-gray-500"
        />
        {isAdmin && (
          <div className="flex items-center gap-3 mt-2">
            <button
              onClick={() => save('firm')}
              disabled={savingScope === 'firm'}
              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {savingScope === 'firm' ? 'Saving…' : 'Save firm guidance'}
            </button>
            {savedScope === 'firm' && <span className="text-xs text-green-600">Saved ✓</span>}
          </div>
        )}
      </div>

      {/* Personal override */}
      <div className="bg-white border rounded-lg p-4">
        <div className="mb-2">
          <h3 className="text-sm font-semibold text-gray-800">
            My personal override {hasUserOverride && <span className="ml-1 text-[10px] font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-full px-1.5 py-0.5">active</span>}
          </h3>
          <p className="text-xs text-gray-500">Optional. When filled in, this replaces the firm-wide guidance for you only. Leave empty to use the firm default.</p>
        </div>
        <textarea
          value={userText}
          onChange={(e) => setUserText(e.target.value)}
          rows={7}
          placeholder="Leave empty to inherit the firm-wide taxonomy above."
          className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-y"
        />
        <div className="flex items-center gap-3 mt-2">
          <button
            onClick={() => save('user')}
            disabled={savingScope === 'user'}
            className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {savingScope === 'user' ? 'Saving…' : 'Save my override'}
          </button>
          {savedScope === 'user' && <span className="text-xs text-green-600">Saved ✓</span>}
        </div>
      </div>

      <p className="text-xs text-gray-400">
        Tip: keep it concise and concrete. The assistant already reads your existing tag library — this
        just tells it how to organize new tagging.
      </p>
    </div>
  );
}
